import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { BsArrowLeft, BsKanban, BsClockHistory } from 'react-icons/bs';
import { useCustomer } from './CustomerContext';
import StatusBadge from './components/StatusBadge';
import EmptyState from './components/EmptyState';
import { apiFetch, parseJsonSafe, formatDate } from '../../utils/api';
import ExpandableText from '../../components/ExpandableText';

const backLink = (
  <Link
    to="/cliente/proyectos"
    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--cp-accent)', textDecoration: 'none', fontWeight: 500, marginBottom: 14 }}
  >
    <BsArrowLeft /> Proyectos
  </Link>
);

const attachmentChipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  maxWidth: '100%',
  padding: '4px 10px',
  borderRadius: 8,
  border: '1px solid var(--cp-border)',
  background: '#f4f8fc',
  color: 'var(--cp-accent)',
  fontSize: '0.8rem',
  fontWeight: 500,
  cursor: 'pointer',
  textAlign: 'left'
};

const CustomerProjectDetailPage = () => {
  const { id } = useParams();
  const { authHeaders, logout } = useCustomer();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(() => setReloadKey((k) => k + 1), []);

  const downloadAttachment = async (kind, att) => {
    const url = kind === 'followup'
      ? `/api/v1/software/customer/portal/followup-attachments/${att.id}/download`
      : `/api/v1/software/customer/portal/task-attachments/${att.id}/download`;
    try {
      const res = await apiFetch(url, { headers: authHeaders() });
      if (res.status === 401) { logout(); return; }
      if (!res.ok) { const d = await parseJsonSafe(res); throw new Error((d && d.message) || 'No se pudo descargar el archivo'); }
      const blob = await res.blob();
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u;
      a.download = att.filename || 'archivo';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(u);
    } catch (e) {
      alert(e.message);
    }
  };

  const renderAttachments = (kind, listRaw) => {
    const items = Array.isArray(listRaw) ? listRaw : [];
    if (items.length === 0) return null;
    return (
      <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {items.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => downloadAttachment(kind, a)}
            title={`Descargar ${a.filename}`}
            style={attachmentChipStyle}
          >
            <span aria-hidden="true">📎</span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.filename}</span>
          </button>
        ))}
      </div>
    );
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await apiFetch(`/api/v1/software/customer/portal/projects/${id}`, { headers: authHeaders() });
        if (res.status === 401) { logout(); return; }
        const data = await parseJsonSafe(res);
        if (!alive) return;
        if (!res.ok) throw new Error((data && data.message) || 'No se pudo cargar el proyecto');
        setProject(data);
      } catch (e) {
        if (!alive) return;
        setError(e.message);
        setProject(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id, authHeaders, logout, reloadKey]);

  if (loading) {
    return (
      <div>
        <div className="cp-skeleton cp-skeleton-title" />
        <div className="cp-skeleton cp-skeleton-card" style={{ height: 300 }} />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div>
        {backLink}
        <EmptyState icon={BsKanban} title="Proyecto no disponible" subtitle={error || 'No se ha encontrado el proyecto.'} actionLabel="Reintentar" onAction={load} />
      </div>
    );
  }

  const tasks = Array.isArray(project.tasks) ? project.tasks : [];
  const followups = Array.isArray(project.followups) ? project.followups : [];
  const done = tasks.filter((t) => t.estado === 'realizada').length;
  const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;

  return (
    <div>
      {backLink}

      <div className="cp-card-static" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <h1 className="cp-page-title" style={{ margin: 0 }}>{project.titulo}</h1>
          <StatusBadge status={project.estado} />
        </div>
        <div style={{ display: 'flex', gap: 24, color: 'var(--cp-muted)', fontSize: '0.85rem', marginTop: 8, flexWrap: 'wrap' }}>
          <span>Inicio: {formatDate(project.fecha_inicio)}</span>
          <span>Fin: {project.fecha_fin ? formatDate(project.fecha_fin) : (project.estado === 'finalizado' ? '-' : 'En curso')}</span>
        </div>
        {tasks.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--cp-muted)', marginBottom: 4 }}>{done}/{tasks.length} tareas completadas · {pct}%</div>
            <div style={{ height: 8, borderRadius: 999, background: '#eceff3', overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg,#50BAFC,#2E9BE0)', borderRadius: 999, transition: 'width .3s ease' }} />
            </div>
          </div>
        )}
      </div>

      <h2 className="cp-page-title" style={{ fontSize: 20, marginBottom: 14 }}>Seguimiento</h2>
      {followups.length === 0 ? (
        <EmptyState icon={BsClockHistory} title="Sin avances publicados" subtitle="Aún no hay avances visibles de este proyecto." />
      ) : (
        followups.map((f) => (
          <div key={f.id} className="cp-card-static" style={{ padding: 16, marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <strong style={{ color: 'var(--cp-text)' }}>{f.titulo}</strong>
              <span style={{ color: 'var(--cp-muted)', fontSize: '0.82rem' }}>{formatDate(f.fecha)}</span>
            </div>
            <div style={{ marginTop: 8 }}><ExpandableText text={f.notas} title={f.titulo} /></div>
            {renderAttachments('followup', f.attachments)}
          </div>
        ))
      )}

      {tasks.length > 0 && (
        <>
          <h2 className="cp-page-title" style={{ fontSize: 20, margin: '24px 0 14px' }}>Tareas</h2>
          <div className="cp-card-static" style={{ padding: 6 }}>
            {tasks.map((t) => (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid var(--cp-border)' }}>
                <span
                  aria-hidden="true"
                  style={{
                    width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                    border: '2px solid', borderColor: t.estado === 'realizada' ? '#198754' : '#cbd5e1',
                    background: t.estado === 'realizada' ? '#198754' : 'transparent', color: '#fff',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700
                  }}
                >
                  {t.estado === 'realizada' ? '✓' : ''}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ textDecoration: t.estado === 'realizada' ? 'line-through' : 'none', color: t.estado === 'realizada' ? 'var(--cp-muted)' : 'var(--cp-text)' }}>{t.titulo}</span>
                  {renderAttachments('task', t.attachments)}
                </div>
                <span style={{ marginLeft: 'auto' }}><StatusBadge status={t.estado} /></span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerProjectDetailPage;
